import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  Retreat,
  FilterOptions,
  LoadingState,
  PaginatedResponse,
} from "../../types";
import { retreatService } from "../../services/retreatService";

interface RetreatState extends LoadingState {
  retreats: Retreat[];
  publicRetreats: Retreat[];
  featuredRetreats: Retreat[];
  selectedRetreat: Retreat | null;
  editRetreat: any | null; // For retreat being edited
  filters: FilterOptions;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    pages: number;
  } | null;
  selectedCurrency?: string;
}

const initialState: RetreatState = {
  retreats: [],
  publicRetreats: [],
  featuredRetreats: [],
  selectedRetreat: null,
  editRetreat: null,
  filters: {},
  pagination: null,
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchRetreats = createAsyncThunk(
  "retreats/fetchRetreats",
  async (
    params: { page?: number; limit?: number; filters?: FilterOptions },
    { rejectWithValue }
  ) => {
    try {
      const response = await retreatService.getRetreats(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch retreats"
      );
    }
  }
);

export const fetchFeaturedRetreats = createAsyncThunk(
  "retreats/fetchFeaturedRetreats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getFeaturedRetreats();
      //@ts-ignore
      // @ts-ignore
      const data = response.data?.items?.map((item: any) => ({
        id: item?._id ?? item?.id ?? "",
        title: item?.title ?? "",
        description:
          item?.retreatInfo?.description ?? item?.basicInfo?.description ?? "",
        destination: {
          name: item?.basicInfo?.location ?? "",
          country: item?.basicInfo?.location?.split(",").pop()?.trim() ?? "",
        },
        price: item?.priceAndDates?.basePrice?.fullPrice ?? 0,
        startDate: item?.priceAndDates?.dates?.[0]?.startDate ?? null,
        endDate: item?.priceAndDates?.dates?.[0]?.endDate ?? null,
        images: item?.media?.featured ?? [],
        isActive: !!item?.isActive,
        hotOffer: !!item?.priceAndDates?.flashSale,
        leavingSoon: false,
        recentlyAdded: false,
        rating: item?.rating ?? 0,
        ...item
      })) as Retreat[];

      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch featured retreats"
      );
    }
  }
);

export const fetchRetreatById = createAsyncThunk(
  "retreats/fetchRetreatById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await retreatService.getRetreatByIdApi(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch retreat"
      );
    }
  }
);

export const searchRetreats = createAsyncThunk(
  "retreats/searchRetreats",
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await retreatService.searchRetreats(query);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to search retreats"
      );
    }
  }
);

// Get all retreats with status filter for My Retreats and Drafts pages
export const fetchAllRetreats = createAsyncThunk(
  "retreats/fetchAllRetreats",
  async (
    params: {
      page?: number;
      limit?: number;
      status?: "published" | "draft" | "pending";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await retreatService.getAllRetreats(
        params.page || 1,
        params.limit || 10,
        params.status
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch retreats"
      );
    }
  }
);
export const fetchPublicRetreats = createAsyncThunk(
  "retreats/fetchPublicRetreats",
  async (
    params: {
      page?: number;
      limit?: number;
      status?: "published" | "draft" | "pending";
      searchParams?: {
        search?: string;
        location?: string;
        startDate?: string;
        endDate?: string;
      };
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await retreatService.getPublicRetreats(
        params.page || 1,
        params.limit || 10,
        params.status,
        params.searchParams
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch retreats"
      );
    }
  }
);
// Get retreat by ID for editing/viewing
export const fetchRetreatForEdit = createAsyncThunk(
  "retreats/fetchRetreatForEdit",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await retreatService.getRetreatByIdApi(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch retreat"
      );
    }
  }
);

// Update retreat
export const updateRetreat = createAsyncThunk(
  "retreats/updateRetreat",
  async (params: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await retreatService.updateRetreatApi(
        params.id,
        params.data
      );
      return { id: params.id, data: response };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update retreat"
      );
    }
  }
);

// Delete retreat
export const deleteRetreat = createAsyncThunk(
  "retreats/deleteRetreat",
  async (id: string, { rejectWithValue }) => {
    try {
      await retreatService.deleteRetreat(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete retreat"
      );
    }
  }
);

const retreatSlice = createSlice({
  name: "retreats",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<FilterOptions>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setSelectedRetreat: (state, action: PayloadAction<Retreat | null>) => {
      state.selectedRetreat = action.payload;
    },
    setEditRetreat: (state, action: PayloadAction<any | null>) => {
      state.editRetreat = action.payload;
    },
    clearRetreats: (state) => {
      state.retreats = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Retreats
      .addCase(fetchRetreats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRetreats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.retreats = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchRetreats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Featured Retreats
      .addCase(fetchPublicRetreats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPublicRetreats.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle the response structure: { data: { data: [...], pagination: {...} } }
        if (action.payload.data && typeof action.payload.data === "object") {
          state.publicRetreats =
            action.payload.data.data || action.payload.data;
          state.pagination = action.payload.data.pagination || null;
        } else {
          state.publicRetreats = action.payload.data || [];
        }
      })
      .addCase(fetchPublicRetreats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFeaturedRetreats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedRetreats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.featuredRetreats = action.payload;
      })
      .addCase(fetchFeaturedRetreats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Retreat by ID
      .addCase(fetchRetreatById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRetreatById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedRetreat = action.payload;
      })
      .addCase(fetchRetreatById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Search Retreats
      .addCase(searchRetreats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchRetreats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.retreats = action.payload;
      })
      .addCase(searchRetreats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch All Retreats (with status filter)
      .addCase(fetchAllRetreats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRetreats.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle the response structure: { data: { data: [...], pagination: {...} } }
        if (action.payload.data && typeof action.payload.data === "object") {
          state.retreats = action.payload.data.data || action.payload.data;
          state.pagination = action.payload.data.pagination || null;
        } else {
          state.retreats = action.payload.data || [];
        }
      })
      .addCase(fetchAllRetreats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Retreat for Edit
      .addCase(fetchRetreatForEdit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRetreatForEdit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.editRetreat = action.payload.data;
      })
      .addCase(fetchRetreatForEdit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update Retreat
      .addCase(updateRetreat.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRetreat.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the retreat in the list if it exists
        const index = state.retreats.findIndex(
          (r) => r.id === action.payload.id
        );
        if (index !== -1) {
          state.retreats[index] = action.payload.data.data;
        }
        // Update editRetreat if it's the same one
        if (state.editRetreat && state.editRetreat.id === action.payload.id) {
          state.editRetreat = action.payload.data.data;
        }
      })
      .addCase(updateRetreat.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete Retreat
      .addCase(deleteRetreat.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRetreat.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the retreat from the list
        state.retreats = state.retreats.filter((r) => r.id !== action.payload);
        // Clear editRetreat if it's the deleted one
        if (state.editRetreat && state.editRetreat.id === action.payload) {
          state.editRetreat = null;
        }
      })
      .addCase(deleteRetreat.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setFilters,
  clearFilters,
  setSelectedRetreat,
  setEditRetreat,
  clearRetreats,
} = retreatSlice.actions;

export default retreatSlice.reducer;
